/**
 * Drawing one frame of the Memory control (D340): the backdrop of the picture (the globe's shells and rings, the map's
 * guides, spokes and captions, the Timeline's months and lanes), the selection's connections, every node in the glyph of
 * its layer, and the labels. Canvas 2D; what is not lit steps back; the far side of the globe is dimmer.
 *
 * Ported from RUBRIC Agentic OS v2 (Jay E, RoboNuggets, MIT; see THIRD-PARTY-NOTICES.md): brain/port.js `drawShells`
 * (faint rings, a wash per department, glowing layer names), `drawEdges` and the focus dimming of `_core.js`
 * (`focusSet` / `focusLinks`: the selection's neighbours lit and linked, the rest at a few percent), `drawMapLabels`
 * (captions that move up rather than overlap), brain/timeline-layout.js `draw`. The look is ours: tier colours, the
 * department tones, Inter, one accent for the selection and the present moment.
 */
import {
  bowControl,
  drawApp,
  drawBubble,
  drawLayerLabel,
  drawNameLabel,
  drawOuter,
  drawRoutine,
  drawSelection,
  drawSkill,
  drawStruct,
  glowSprite,
  type NodeStyle,
  orbSprite,
  rgba,
} from './memoryBrainDraw.js';
import { bubbleFactor, type Engine, globeFrame, type Picture, projectGlobe, type Stage } from './memoryBrainEngine.js';
import { type GlobeColours, nodeColour } from './memoryGlobeColours.js';
import { type Connection, connectionAlpha, type Globe, type GlobeLayer, isFoldable, isRingLayer, LAYER_WORDS, LINK_WEIGHT, type LinkKind, RING_LAYERS, ringVector } from './memoryGlobeModel.js';
import { hubLabelBudget, labelBudget } from './memoryLabelModel.js';
import type { MapLayout } from './memoryMapLayouts.js';
import { changedWords, type TimelineLayout } from './memoryTimelineModel.js';

const TAU = Math.PI * 2;
const DIM = 0.13;

export interface LabelHit {
  layer: GlobeLayer;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface PaintInput {
  globe: Globe;
  colours: GlobeColours;
  stage: Stage;
  picture: Picture;
  map: MapLayout | null;
  tl: TimelineLayout | null;
  tlX: number;
  dpr: number;
  selected: number;
  pointed: number;
  /** Node indices that answer a search or an answer; null: nothing is being looked for. */
  found: ReadonlySet<number> | null;
  connections: readonly Connection[];
  /** A connection pointed at in the card: its link is drawn heavy with its words. */
  litLink: { index: number; relation: string } | null;
  /** Nodes of the group a legend entry focused; the rest step back. Null: none focused. */
  focus: ReadonlySet<number> | null;
  /** Folders that are open, by key. */
  expanded: ReadonlySet<string>;
  /** The graph's own links and the layers' links as node-index pairs, drawn faint (`wire`) and dashed (`xlink`). */
  wires: ReadonlyArray<readonly [number, number]>;
  xlinks: ReadonlyArray<readonly [number, number]>;
  offLayers: ReadonlySet<GlobeLayer>;
  offDepts: ReadonlySet<number>;
  motion: boolean;
  /** The animation clock, seconds. */
  now: number;
  /** The wall clock, ms, for "changed 3 days ago". */
  wall: number;
  /** The instruction file named at the centre, and the project or organisation above it. */
  centreFile: string;
  centreOwner?: string;
  font: string;
}

export interface PaintResult {
  labels: LabelHit[];
}

const isBubble = (weight: number) => weight >= 6;

/** One frame. */
export function paint(ctx: CanvasRenderingContext2D, e: Engine, p: PaintInput): PaintResult {
  const { W, H } = p.stage;
  const labels: LabelHit[] = [];
  ctx.setTransform(p.dpr, 0, 0, p.dpr, 0, 0);
  ctx.clearRect(0, 0, W, H);
  ctx.textBaseline = 'alphabetic';

  if (p.picture === 'globe') drawGlobeBackdrop(ctx, e, p);
  else if (p.picture === 'map' && p.map) drawMapBackdrop(ctx, e, p, p.map);
  else if (p.picture === 'timeline' && p.tl) drawTimelineBackdrop(ctx, e, p, p.tl);

  drawCentreGlow(ctx, e, p);
  drawStructure(ctx, e, p);
  if (p.picture === 'map' && p.map?.rim) drawRimLinks(ctx, e, p, p.map);
  drawConnections(ctx, e, p);
  drawNodes(ctx, e, p);
  drawMarks(ctx, e, p);

  if (p.picture === 'globe') drawGlobeLabels(ctx, e, p, labels);
  else if (p.picture === 'map' && p.map) drawMapLabels(ctx, e, p, p.map, labels);
  else if (p.picture === 'timeline' && p.tl) drawTimelineText(ctx, e, p, p.tl);
  if (p.picture !== 'timeline') drawHubLabels(ctx, e, p, labels);
  if (p.picture === 'map' && p.map?.rim) drawRimArcNames(ctx, e, p, p.map);
  drawNodeNames(ctx, e, p);
  drawPills(ctx, e, p);
  return { labels };
}

/* ── Backdrops ───────────────────────────────────────────────────────── */

function drawCentreGlow(ctx: CanvasRenderingContext2D, e: Engine, p: PaintInput): void {
  const { cx, cy } = e;
  if (!Number.isFinite(cx) || !Number.isFinite(cy)) return;
  ctx.globalAlpha = 0.55;
  ctx.drawImage(glowSprite(p.colours.accent), cx - 52, cy - 52, 104, 104);
  ctx.globalAlpha = 0.5;
  ctx.strokeStyle = p.colours.accent;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(cx, cy, 31, 0, TAU);
  ctx.stroke();
  ctx.globalAlpha = 1;
}

function polyline(ctx: CanvasRenderingContext2D, pts: Array<{ x: number; y: number; z: number }>, front: boolean): void {
  ctx.beginPath();
  let on = false;
  for (const pt of pts) {
    if (front ? pt.z >= 0 : pt.z < 0) {
      if (on) ctx.lineTo(pt.x, pt.y);
      else ctx.moveTo(pt.x, pt.y);
      on = true;
    } else on = false;
  }
  ctx.stroke();
}

function drawGlobeBackdrop(ctx: CanvasRenderingContext2D, e: Engine, p: PaintInput): void {
  const { globe, colours: c } = p;
  const frame = globeFrame(p.stage, globe.outer);
  const cam = e.cam;
  const proj = (v: { x: number; y: number; z: number }) => projectGlobe(v, cam, frame);
  // A level open: the shells are compressed into a rim, and the opened level's arcs are drawn on a ring inside it.
  const radii = e.rim?.radii ?? globe.radii;
  ctx.lineWidth = 0.8;
  // The skills' sphere: a few latitudes and meridians.
  if (globe.counts.skills && !p.offLayers.has('skills')) {
    ctx.strokeStyle = rgba(c.layer.skills, 0.1);
    const rS = radii.skills;
    for (const lat of [-0.67, 0, 0.67]) {
      const pts = Array.from({ length: 73 }, (_, i) => {
        const a = (i / 72) * TAU;
        const rr = rS * Math.sqrt(1 - lat * lat);
        return proj({ x: Math.cos(a) * rr, y: lat * rS, z: Math.sin(a) * rr });
      });
      polyline(ctx, pts, false);
      polyline(ctx, pts, true);
    }
  }
  // The memory sphere: a wash per department on the front, the meridians between them.
  if (globe.counts.memory && !p.offLayers.has('memory')) {
    const rM = radii.memory;
    for (const s of globe.sectors) {
      if (p.offDepts.has(s.deptIndex)) continue;
      const mid = (s.start + s.end) / 2;
      const q = proj({ x: Math.cos(mid) * rM, y: 0, z: Math.sin(mid) * rM });
      if (q.z < -frame.r * 0.1) continue;
      const rad = frame.r * cam.zoom * rM * Math.max(0.22, Math.min(0.55, (s.end - s.start) * 0.9));
      ctx.globalAlpha = 0.07 * (0.5 + 0.5 * Math.max(0, q.z / (frame.r * cam.zoom * rM)));
      ctx.drawImage(glowSprite(c.dept[s.deptIndex] ?? c.layer.memory), q.x - rad, q.y - rad, rad * 2, rad * 2);
    }
    ctx.globalAlpha = 1;
    ctx.strokeStyle = rgba(c.layer.memory, 0.14);
    for (const s of globe.sectors) {
      const pts = Array.from({ length: 49 }, (_, i) => {
        const lat = -Math.PI / 2 + (Math.PI * i) / 48;
        return proj({ x: Math.cos(lat) * Math.cos(s.start) * rM, y: Math.sin(lat) * rM, z: Math.cos(lat) * Math.sin(s.start) * rM });
      });
      polyline(ctx, pts, true);
    }
    const r = frame.r * cam.zoom * rM;
    ctx.strokeStyle = rgba(c.layer.memory, 0.2);
    ctx.beginPath();
    ctx.arc(frame.cx + cam.panX, frame.cy + cam.panY, r, 0, TAU);
    ctx.stroke();
  }
  // The rings: Applications, Routines and our five outer layers, fainter behind, firmer in front.
  for (const k of RING_LAYERS) {
    if (!globe.counts[k] || p.offLayers.has(k)) continue;
    const pts = Array.from({ length: 121 }, (_, i) => {
      const v = ringVector(k, (i / 120) * TAU, e.rings);
      const r = radii[k];
      return proj({ x: v.x * r, y: v.y * r, z: v.z * r });
    });
    ctx.strokeStyle = rgba(c.layer[k], 0.1);
    polyline(ctx, pts, false);
    ctx.strokeStyle = rgba(c.layer[k], 0.3);
    polyline(ctx, pts, true);
  }
  if (e.rim) {
    ctx.lineWidth = 2.2;
    for (const a of e.rim.arcs) {
      const steps = Math.max(8, Math.ceil(((a.a1 - a.a0) / TAU) * 120));
      const pts = Array.from({ length: steps + 1 }, (_, i) => {
        const ang = a.a0 + ((a.a1 - a.a0) * i) / steps;
        return proj({ x: Math.cos(ang) * a.r, y: -Math.sin(ang) * a.r, z: 0 });
      });
      ctx.strokeStyle = rgba(c.layer[a.layer], 0.55);
      polyline(ctx, pts, true);
      ctx.strokeStyle = rgba(c.layer[a.layer], 0.2);
      polyline(ctx, pts, false);
    }
    ctx.lineWidth = 0.8;
  }
}

function drawGlobeLabels(ctx: CanvasRenderingContext2D, e: Engine, p: PaintInput, hits: LabelHit[]): void {
  const { globe, colours: c } = p;
  const frame = globeFrame(p.stage, globe.outer);
  const place = (layer: GlobeLayer, x: number, y: number, align: CanvasTextAlign, z = 1) => {
    const text = LAYER_WORDS[layer];
    const { w, h } = drawLayerLabel(ctx, text, x, y, c.layer[layer], { align, alpha: z < 0 ? 0.5 : 0.92 });
    const x0 = align === 'left' ? x : align === 'right' ? x - w : x - w / 2;
    hits.push({ layer, x: x0 - 6, y: y - h - 4, w: w + 12, h: h + 12 });
  };
  const used: Array<{ x: number; y: number }> = [];
  const nudge = (x: number, y: number) => {
    let yy = y;
    for (let i = 0; i < 6 && used.some((u) => Math.abs(u.x - x) < 110 && Math.abs(u.y - yy) < 15); i++) yy -= 15;
    used.push({ x, y: yy });
    return yy;
  };
  if (e.rim) {
    for (const a of e.rim.arcs) {
      if (!a.label) continue;
      const mid = (a.a0 + a.a1) / 2;
      const q = projectGlobe({ x: Math.cos(mid) * (a.r - 0.05), y: -Math.sin(mid) * (a.r - 0.05), z: 0 }, e.cam, frame);
      drawNameLabel(ctx, `${a.label.length > 20 ? `${a.label.slice(0, 19)}…` : a.label} · ${a.count.toLocaleString('en-AU')}`, q.x, q.y + 4, { colour: c.layer[a.layer], size: 11, bold: true, alpha: q.z < 0 ? 0.45 : 1 });
    }
  }
  if (globe.counts.memory && !p.offLayers.has('memory')) {
    const q = projectGlobe({ x: 0, y: (e.rim?.radii ?? globe.radii).memory, z: 0 }, e.cam, frame);
    place('memory', q.x, nudge(q.x, q.y - 12), 'center');
  }
  if (globe.counts.skills && !p.offLayers.has('skills')) {
    // Above the logo, on the skills' sphere, so the instruction file's name under the logo stays clear.
    const q = projectGlobe({ x: 0, y: (e.rim?.radii ?? globe.radii).skills, z: 0 }, e.cam, frame);
    place('skills', q.x, nudge(q.x, q.y - 22), 'center');
  }
  RING_LAYERS.forEach((k, idx) => {
    if (!globe.counts[k] || p.offLayers.has(k)) return;
    // A ring's name sits on the ring, spread round it so no two meet: Applications to the left, Routines to the right.
    const angle = k === 'apps' ? Math.PI : k === 'routines' ? 0 : idx % 2 ? 0 : Math.PI;
    const v = ringVector(k, angle, e.rings);
    const rad = (e.rim?.radii ?? globe.radii)[k];
    const q = projectGlobe({ x: v.x * (rad + 0.06), y: v.y * (rad + 0.06), z: v.z * (rad + 0.06) }, e.cam, frame);
    const align: CanvasTextAlign = q.x < frame.cx - 40 ? 'right' : q.x > frame.cx + 40 ? 'left' : 'center';
    place(k, q.x, nudge(q.x, q.y - 6), align, q.z);
  });
}

function drawMapBackdrop(ctx: CanvasRenderingContext2D, e: Engine, p: PaintInput, m: MapLayout): void {
  const { colours: c } = p;
  const k = e.flat.k;
  ctx.save();
  ctx.translate(e.flat.x, e.flat.y);
  ctx.scale(k, k);
  ctx.lineWidth = 1 / k;
  if (m.bound) {
    ctx.strokeStyle = rgba(c.muted, 0.3);
    ctx.beginPath();
    if (m.bound.type === 'circle') ctx.arc(0, 0, m.bound.R, 0, TAU);
    else {
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * TAU;
        const x = Math.cos(a) * m.bound.R;
        const y = Math.sin(a) * m.bound.R;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
    }
    ctx.stroke();
  }
  for (const g of m.guides) {
    if (p.offLayers.has(g.layer)) continue;
    ctx.strokeStyle = rgba(c.layer[g.layer], g.layer === 'memory' ? 0.1 : 0.2);
    ctx.beginPath();
    ctx.arc(0, 0, g.r, 0, TAU);
    ctx.stroke();
  }
  // The rim state: the opened ring's arcs, a heavier hairline under each, with a tick where one ends.
  if (m.rim) {
    const u = m.unit;
    ctx.lineCap = 'round';
    for (const a of m.rim.arcs) {
      const col = a.deptIndex >= 0 ? (c.dept[a.deptIndex] ?? c.layer[a.layer]) : c.layer[a.layer];
      ctx.strokeStyle = rgba(col, 0.55);
      ctx.lineWidth = 2.2 / k;
      ctx.beginPath();
      ctx.arc(0, 0, a.r0 - 12 * u, a.a0, a.a1);
      ctx.stroke();
      ctx.lineWidth = 1 / k;
      ctx.strokeStyle = rgba(col, 0.25);
      ctx.beginPath();
      ctx.moveTo(Math.cos(a.a0) * (a.r0 - 16 * u), Math.sin(a.a0) * (a.r0 - 16 * u));
      ctx.lineTo(Math.cos(a.a0) * (a.r1 + 10 * u), Math.sin(a.a0) * (a.r1 + 10 * u));
      ctx.stroke();
    }
  }
  ctx.restore();
}

/** The rim state's counted links: from each arc to the hub it belongs to, and across to the hubs the graph links it to. */
function drawRimLinks(ctx: CanvasRenderingContext2D, e: Engine, p: PaintInput, m: MapLayout): void {
  const rim = m.rim;
  if (!rim) return;
  const { colours: c } = p;
  const u = m.unit;
  const k = e.flat.k;
  const toScreen = (x: number, y: number) => ({ x: x * k + e.flat.x, y: y * k + e.flat.y });
  ctx.save();
  ctx.lineCap = 'round';
  const labels: Array<{ x: number; y: number; text: string; colour: string }> = [];
  rim.links.forEach((l, i) => {
    const arc = rim.arcs[l.arc];
    if (!arc || e.A[l.to] < 0.5) return;
    const mid = (arc.a0 + arc.a1) / 2;
    const from = toScreen(Math.cos(mid) * (arc.r1 + 14 * u), Math.sin(mid) * (arc.r1 + 14 * u));
    const to = { x: e.X[l.to], y: e.Y[l.to] };
    const col = l.own ? (arc.deptIndex >= 0 ? (c.dept[arc.deptIndex] ?? c.layer[arc.layer]) : c.layer[arc.layer]) : nodeColour(c, p.globe.nodes[l.to].layer, p.globe.nodes[l.to].deptIndex);
    const [cx, cy] = bowControl(from.x, from.y, to.x, to.y, i);
    ctx.setLineDash(l.own ? [] : [4, 4]);
    ctx.strokeStyle = rgba(col, l.own ? 0.34 : 0.4);
    ctx.lineWidth = l.own ? 1.2 : 1;
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.quadraticCurveTo(cx, cy, to.x, to.y);
    ctx.stroke();
    const t = l.own ? 0.3 : 0.5;
    labels.push({ x: (1 - t) * (1 - t) * from.x + 2 * t * (1 - t) * cx + t * t * to.x, y: (1 - t) * (1 - t) * from.y + 2 * t * (1 - t) * cy + t * t * to.y, text: l.count.toLocaleString('en-AU'), colour: col });
  });
  ctx.setLineDash([]);
  ctx.restore();
  for (const l of labels) drawNameLabel(ctx, l.text, l.x, l.y + 3.5, { colour: l.colour, size: 10, bold: true, alpha: 0.95 });
}

/** The names of the opened ring's arcs, written inside the ring where each begins to open: "In progress · 12". */
function drawRimArcNames(ctx: CanvasRenderingContext2D, e: Engine, p: PaintInput, m: MapLayout): void {
  const rim = m.rim;
  if (!rim) return;
  const { colours: c } = p;
  const u = m.unit;
  const k = e.flat.k;
  for (const a of rim.arcs) {
    if (!a.label) continue;
    const mid = (a.a0 + a.a1) / 2;
    const r = a.r0 - 26 * u;
    const x = Math.cos(mid) * r * k + e.flat.x;
    const y = Math.sin(mid) * r * k + e.flat.y;
    const chord = (a.a1 - a.a0) * r * k;
    const text = `${a.label.length > 20 ? `${a.label.slice(0, 19)}…` : a.label} · ${a.count.toLocaleString('en-AU')}`;
    ctx.font = `600 11px ${p.font}`;
    const w = ctx.measureText(text).width;
    if (chord < Math.min(w * 0.7, 60)) continue;
    const col = a.deptIndex >= 0 ? (c.dept[a.deptIndex] ?? c.layer[a.layer]) : c.layer[a.layer];
    drawNameLabel(ctx, text, x, y + 4, { colour: col, size: 11, bold: true });
  }
}

function drawMapLabels(ctx: CanvasRenderingContext2D, e: Engine, p: PaintInput, m: MapLayout, hits: LabelHit[]): void {
  const { colours: c } = p;
  const { W, H } = p.stage;
  const toScreen = (x: number, y: number) => ({ x: x * e.flat.k + e.flat.x, y: y * e.flat.k + e.flat.y });
  const used: Array<{ x: number; y: number; w: number }> = [];
  const fit = (x: number, y: number, w: number): number => {
    let yy = y;
    for (let tries = 0; tries < 8 && used.some((u) => Math.abs(u.x - x) < (u.w + w) / 2 && Math.abs(u.y - yy) < 14); tries++) yy -= 14;
    used.push({ x, y: yy, w });
    return yy;
  };
  for (const cap of m.captions) {
    const a = toScreen(cap.x, cap.y);
    if (a.x < 24 || a.x > W - 24 || a.y < p.stage.top || a.y > H - 12) continue;
    if (cap.kind === 'layer') {
      if (p.offLayers.has(cap.layer!)) continue;
      const w = LAYER_WORDS[cap.layer!].length * 8;
      const y = fit(a.x, a.y, w);
      const { w: tw, h } = drawLayerLabel(ctx, LAYER_WORDS[cap.layer!], a.x, y, c.layer[cap.layer!]);
      hits.push({ layer: cap.layer!, x: a.x - tw / 2 - 6, y: y - h - 4, w: tw + 12, h: h + 12 });
    } else if (cap.deptIndex !== undefined && !p.offDepts.has(cap.deptIndex) && !p.offLayers.has('memory')) {
      const col = c.dept[cap.deptIndex] ?? c.text;
      ctx.font = `600 12px ${p.font}`;
      const w = ctx.measureText(cap.text).width + 8;
      const y = fit(a.x, a.y, w);
      drawNameLabel(ctx, cap.text, a.x, y, { colour: col, size: 12, bold: true });
    }
  }
}

function drawTimelineBackdrop(ctx: CanvasRenderingContext2D, e: Engine, p: PaintInput, tl: TimelineLayout): void {
  const { colours: c } = p;
  const k = e.flat.k;
  ctx.save();
  ctx.translate(e.flat.x + p.tlX * k, e.flat.y);
  ctx.scale(k, k);
  const b = tl.bounds;
  const top = b.top - 6;
  const bottom = b.bottom + 4;
  const hair = 1 / k;
  ctx.fillStyle = rgba(c.text, 0.03);
  for (const m of tl.months) ctx.fillRect(m.x, top, m.x1 - m.x, bottom - top);
  ctx.fillStyle = rgba(c.text, 0.025);
  ctx.fillRect(b.olderX, top, b.olderW, bottom - top);
  ctx.lineWidth = hair;
  ctx.strokeStyle = rgba(c.muted, 0.12);
  ctx.beginPath();
  for (const d of tl.days) {
    if (d.day === 1) continue;
    ctx.moveTo(d.x, top);
    ctx.lineTo(d.x, bottom);
  }
  ctx.stroke();
  ctx.strokeStyle = rgba(c.muted, 0.4);
  ctx.beginPath();
  for (const m of tl.months) {
    ctx.moveTo(m.x, top);
    ctx.lineTo(m.x, bottom);
    ctx.moveTo(m.x1, top);
    ctx.lineTo(m.x1, bottom);
  }
  ctx.moveTo(b.olderX, top);
  ctx.lineTo(b.olderX, bottom);
  ctx.moveTo(b.olderX + b.olderW, top);
  ctx.lineTo(b.olderX + b.olderW, bottom);
  ctx.stroke();
  if (tl.today) {
    const m = tl.months.at(-1)!;
    ctx.fillStyle = 'rgba(0,0,0,0.35)';
    ctx.fillRect(tl.today.x, top, m.x1 - tl.today.x, bottom - top);
  }
  // Lane floors, and a lane's colour as a hairline along its foot.
  ctx.beginPath();
  ctx.strokeStyle = rgba(c.muted, 0.2);
  for (const l of tl.lanes) {
    ctx.moveTo(b.olderX, l.bottom);
    ctx.lineTo(b.right, l.bottom);
  }
  for (const cl of tl.clusters.slice(0, -1)) {
    ctx.moveTo(b.olderX, cl.bottom + 4);
    ctx.lineTo(b.right, cl.bottom + 4);
  }
  ctx.stroke();
  // The AGENTS md lane: its baseline and an tick for every edit day.
  const inst = tl.lanes.find((l) => l.key === 'instructions');
  if (inst) {
    const y = (inst.top + inst.bottom) / 2;
    ctx.strokeStyle = rgba(c.accent, 0.3);
    ctx.beginPath();
    ctx.moveTo(b.olderX, y);
    ctx.lineTo(b.right, y);
    ctx.stroke();
    ctx.strokeStyle = c.accent;
    ctx.lineWidth = Math.max(1.4, 1.8 / k);
    ctx.lineCap = 'round';
    ctx.beginPath();
    for (const t of tl.ticks) {
      const h = 5 + Math.min(5, t.count - 1) * 1.6;
      ctx.moveTo(t.x, y - h);
      ctx.lineTo(t.x, y + h);
    }
    ctx.stroke();
    ctx.lineCap = 'butt';
    if (tl.olderEdits) {
      ctx.fillStyle = rgba(c.accent, 0.9);
      ctx.font = `500 ${10 / k}px ${p.font}`;
      ctx.textAlign = 'left';
      ctx.fillText(`${tl.olderEdits} older edits`, b.olderX + 6, y + 3.5 / k);
    }
  }
  // Now: the accent, with its glow.
  if (tl.today) {
    ctx.strokeStyle = c.accent;
    ctx.globalAlpha = 0.85;
    ctx.lineWidth = hair * 1.2;
    ctx.beginPath();
    ctx.moveTo(tl.today.x, top);
    ctx.lineTo(tl.today.x, bottom);
    ctx.stroke();
    ctx.globalAlpha = 1;
  }
  ctx.restore();
}

function drawTimelineText(ctx: CanvasRenderingContext2D, e: Engine, p: PaintInput, tl: TimelineLayout): void {
  const { colours: c, globe: g } = p;
  const k = e.flat.k;
  const sx = (x: number) => (x + p.tlX) * k + e.flat.x;
  const sy = (y: number) => y * k + e.flat.y;
  const b = tl.bounds;
  const font = p.font;
  ctx.lineJoin = 'round';
  ctx.font = `600 11px ${font}`;
  try {
    ctx.letterSpacing = '2.2px';
  } catch {
    // Unspaced on an older engine.
  }
  ctx.textAlign = 'left';
  ctx.fillStyle = c.text;
  const headY = sy(b.top - 30);
  for (const m of tl.months) {
    const x0 = sx(m.x);
    const x1 = sx(m.x1);
    if (x1 < 0 || x0 > p.stage.W) continue;
    const full = `${m.label.toUpperCase()}  ${m.year}`;
    const room = x1 - x0 - 10;
    const label = ctx.measureText(full).width <= room ? full : ctx.measureText(m.label.slice(0, 3).toUpperCase()).width <= room ? m.label.slice(0, 3).toUpperCase() : '';
    if (label) ctx.fillText(label, Math.max(x0 + 4, Math.min(sx(b.olderX + b.olderW) + 4, x1 - ctx.measureText(label).width - 6)), headY);
  }
  ctx.fillStyle = c.muted;
  ctx.fillText('OLDER', sx(b.olderX) + 4, headY);
  try {
    ctx.letterSpacing = '0px';
  } catch {
    // As above.
  }
  ctx.font = `400 10px ${font}`;
  ctx.fillText('incl. undated', sx(b.olderX) + 4, headY + 14);
  const dw = tl.dayW * k;
  const step = dw >= 13 ? 1 : dw >= 7 ? 2 : dw >= 3.5 ? 5 : 10;
  ctx.textAlign = 'center';
  ctx.font = `500 ${Math.max(8, Math.min(11, dw * 0.68))}px ${font}`;
  for (const d of tl.days) {
    if ((d.day - 1) % step && d.day !== 1) continue;
    const isToday = tl.today?.key === d.key;
    ctx.fillStyle = isToday ? c.accent : d.dow === 0 || d.dow === 6 ? rgba(c.muted, 0.7) : c.muted;
    ctx.fillText(String(d.day), sx(d.x + tl.dayW / 2), sy(b.top - 10));
  }
  // Lane names (spaced capitals in the layer's colour, glowing a little), and the departments' names down the left.
  ctx.textAlign = 'left';
  for (const l of tl.lanes) {
    if (l.key !== 'instructions' && p.offLayers.has(l.key)) continue;
    const y = l.key === 'memory' ? sy(l.top) + 12 : sy((l.top + l.bottom) / 2) + 4;
    drawLayerLabel(ctx, l.label, sx(28), y, l.key === 'instructions' ? c.accent : c.layer[l.key], { align: 'left', size: 10.5, alpha: 0.9 });
  }
  // Five themes: a bar down the left of each theme's lanes, with its name written up along it.
  for (const t of tl.themes) {
    const x = sx(16);
    const y0 = sy(t.top);
    const y1 = sy(t.bottom);
    ctx.fillStyle = c.layer[t.layer];
    ctx.globalAlpha = 0.85;
    ctx.fillRect(x - 1.5, y0, 3, Math.max(2, y1 - y0));
    ctx.globalAlpha = 1;
    if (y1 - y0 > 70) {
      ctx.save();
      ctx.translate(x - 7, (y0 + y1) / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.textAlign = 'center';
      drawLayerLabel(ctx, t.title, 0, 0, c.layer[t.layer], { size: 9.5, alpha: 0.95 });
      ctx.restore();
    }
  }
  ctx.font = `500 11px ${font}`;
  ctx.textAlign = 'left';
  for (const cl of tl.clusters) {
    const mid = sy((cl.top + cl.bottom) / 2);
    ctx.fillStyle = c.dept[cl.deptIndex] ?? c.text;
    ctx.beginPath();
    ctx.arc(sx(32), mid - 3.5, 3, 0, TAU);
    ctx.fill();
    if ((cl.bottom - cl.top) * k < 11) continue;
    const name = g.departments[cl.deptIndex].label;
    const text = name.length > 15 ? `${name.slice(0, 14)}…` : name;
    ctx.lineWidth = 3;
    ctx.strokeStyle = 'rgba(8,10,14,0.85)';
    ctx.strokeText(text, sx(42), mid);
    ctx.fillStyle = c.text;
    ctx.fillText(text, sx(42), mid);
  }
  if (tl.afterPoint > 0) {
    ctx.fillStyle = c.muted;
    ctx.font = `400 11px ${font}`;
    ctx.textAlign = 'left';
    ctx.fillText(`${tl.afterPoint.toLocaleString()} changed after this day and are not shown`, sx(b.olderX), sy(b.bottom) + 22);
  }
}

/* ── Connections ─────────────────────────────────────────────────────── */

function drawConnections(ctx: CanvasRenderingContext2D, e: Engine, p: PaintInput): void {
  if (p.selected < 0) return;
  drawLitLink(ctx, e, p);
  if (!p.connections.length) return;
  const { globe, colours: c } = p;
  const a = globe.nodes[p.selected];
  const ax = e.X[p.selected];
  const ay = e.Y[p.selected];
  const reach = Math.min(p.stage.W, p.stage.H) * 0.7;
  const colourA = nodeColour(c, a.layer, a.deptIndex);
  ctx.lineCap = 'round';
  p.connections.forEach((cn, i) => {
    if (e.A[cn.index] < 0.2) return;
    const b = globe.nodes[cn.index];
    const bx = e.X[cn.index];
    const by = e.Y[cn.index];
    const dist = Math.hypot(bx - ax, by - ay);
    const alpha = connectionAlpha(cn.hops, dist, reach) * e.A[cn.index];
    const [qx, qy] = bowControl(ax, ay, bx, by, i + 1);
    const grd = ctx.createLinearGradient(ax, ay, bx, by);
    grd.addColorStop(0, rgba(colourA, Math.min(1, alpha * 1.1)));
    grd.addColorStop(1, rgba(nodeColour(c, b.layer, b.deptIndex), alpha));
    ctx.strokeStyle = grd;
    ctx.lineWidth = cn.hops === 1 ? 1.5 : 0.9;
    ctx.beginPath();
    ctx.moveTo(ax, ay);
    ctx.quadraticCurveTo(qx, qy, bx, by);
    ctx.stroke();
  });
  ctx.lineCap = 'butt';
}

/** The link a connection row in the card points at: heavy, in the accent, with its words at the middle. */
function drawLitLink(ctx: CanvasRenderingContext2D, e: Engine, p: PaintInput): void {
  const l = p.litLink;
  if (!l || p.selected < 0 || e.A[l.index] < 0.2) return;
  const ax = e.X[p.selected];
  const ay = e.Y[p.selected];
  const bx = e.X[l.index];
  const by = e.Y[l.index];
  ctx.save();
  ctx.lineCap = 'round';
  ctx.strokeStyle = p.colours.accent;
  ctx.shadowColor = p.colours.accent;
  ctx.shadowBlur = 10;
  ctx.lineWidth = 2.6;
  ctx.beginPath();
  ctx.moveTo(ax, ay);
  ctx.lineTo(bx, by);
  ctx.stroke();
  ctx.restore();
  if (l.relation) {
    ctx.save();
    ctx.font = `600 11px ${p.font}`;
    ctx.textAlign = 'center';
    const w = ctx.measureText(l.relation).width + 14;
    const mx = (ax + bx) / 2;
    const my = (ay + by) / 2;
    ctx.fillStyle = 'rgba(14,17,23,0.94)';
    ctx.strokeStyle = p.colours.accent;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(mx - w / 2, my - 10, w, 20, 10);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = p.colours.text;
    ctx.fillText(l.relation, mx, my + 4);
    ctx.restore();
  }
}

/** The structure: routes from the centre to each layer's hub, spokes from a hub or folder to what hangs from it, the graph's wires and the layers' links. */
function drawStructure(ctx: CanvasRenderingContext2D, e: Engine, p: PaintInput): void {
  if (p.picture === 'timeline') return;
  const { globe, colours: c } = p;
  const rings = p.picture === 'map' && p.map?.kind === 'rings';
  const dimAll = p.selected >= 0 || !!p.found || !!p.focus;
  const focusAlpha = (i: number) => (!dimAll ? 1 : p.selected >= 0 ? 0.35 : p.found ? (p.found.has(i) ? 1 : 0.25) : p.focus!.has(i) ? 1 : 0.2);
  ctx.save();
  ctx.lineCap = 'round';
  // Wires: the graph's own links, very faint, so the clusters read as clusters before one is touched.
  if (p.picture === 'map' && p.wires.length) {
    ctx.strokeStyle = rgba(c.muted, 0.07);
    ctx.lineWidth = LINK_WEIGHT.wire;
    ctx.beginPath();
    const cap = Math.min(p.wires.length, 3500);
    for (let k = 0; k < cap; k++) {
      const [a, b] = p.wires[k];
      if (e.A[a] < 0.9 || e.A[b] < 0.9) continue;
      ctx.moveTo(e.X[a], e.Y[a]);
      ctx.lineTo(e.X[b], e.Y[b]);
    }
    ctx.stroke();
  }
  // Links between layers: dashed.
  if (p.xlinks.length && p.picture === 'map') {
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = rgba(c.muted, 0.3);
    ctx.lineWidth = LINK_WEIGHT.xlink;
    ctx.beginPath();
    for (const [a, b] of p.xlinks) {
      if (e.A[a] < 0.5 || e.A[b] < 0.5) continue;
      ctx.moveTo(e.X[a], e.Y[a]);
      ctx.lineTo(e.X[b], e.Y[b]);
    }
    ctx.stroke();
    ctx.setLineDash([]);
  }
  // Routes from the centre (map pictures).
  if (p.picture === 'map' && p.map && Number.isFinite(e.cx)) {
    for (const i of p.map.centreLinks) {
      if (e.A[i] < 0.15) continue;
      const n = globe.nodes[i];
      const col = nodeColour(c, n.layer, n.deptIndex);
      const grd = ctx.createLinearGradient(e.cx, e.cy, e.X[i], e.Y[i]);
      grd.addColorStop(0, rgba(col, 0.06));
      grd.addColorStop(1, rgba(col, 0.62 * Math.min(1, focusAlpha(i) + 0.2)));
      ctx.strokeStyle = grd;
      ctx.lineWidth = LINK_WEIGHT.route;
      ctx.beginPath();
      ctx.moveTo(e.cx, e.cy);
      ctx.lineTo(e.X[i], e.Y[i]);
      ctx.stroke();
    }
  }
  // Spokes and routes, batched by colour.
  const batches = new Map<string, { colour: string; width: number; alpha: number; segs: number[] }>();
  for (const edge of globe.edges) {
    const a = edge.a;
    const b = edge.b;
    if (e.A[a] < 0.15 || e.A[b] < 0.15) continue;
    const na = globe.nodes[a];
    const nb = globe.nodes[b];
    if (rings && (na.struct !== 'dir' || p.map?.rim)) continue;
    if (p.picture === 'globe' && (na.struct === 'layer' || na.struct === 'theme' || e.Z[a] < -e.gR * 0.25 || e.Z[b] < -e.gR * 0.25)) continue;
    const kind: LinkKind = edge.kind;
    const col = nodeColour(c, nb.layer, nb.deptIndex);
    const f = Math.min(focusAlpha(a), focusAlpha(b));
    const alpha = Math.round((kind === 'route' ? 0.34 : 0.22) * f * Math.min(e.A[a], e.A[b]) * 20) / 20;
    const key = `${kind}|${col}|${alpha}`;
    let batch = batches.get(key);
    if (!batch) batches.set(key, (batch = { colour: col, width: LINK_WEIGHT[kind], alpha, segs: [] }));
    batch.segs.push(e.X[a], e.Y[a], e.X[b], e.Y[b]);
  }
  for (const b of batches.values()) {
    ctx.strokeStyle = rgba(b.colour, b.alpha);
    ctx.lineWidth = b.width;
    ctx.beginPath();
    for (let k = 0; k < b.segs.length; k += 4) {
      ctx.moveTo(b.segs[k], b.segs[k + 1]);
      ctx.lineTo(b.segs[k + 2], b.segs[k + 3]);
    }
    ctx.stroke();
  }
  ctx.restore();
}

/** Names for the layers' and groups' hubs, and for a folder once it is big enough on screen: none over another. */
function drawHubLabels(ctx: CanvasRenderingContext2D, e: Engine, p: PaintInput, hits: LabelHit[]): void {
  const { globe, colours: c } = p;
  const { W, H } = p.stage;
  const used: Array<{ x: number; y: number; w: number; h: number }> = [];
  const free = (x: number, y: number, w: number, h: number) => !used.some((u) => Math.abs(u.x - x) < (u.w + w) / 2 && Math.abs(u.y - y) < (u.h + h) / 2);
  const dimmed = (i: number) => (p.selected >= 0 ? false : p.found ? !p.found.has(i) : p.focus ? !p.focus.has(i) : false);
  // Which groups and folders are named: the biggest few at rest, more as the picture is zoomed in (memoryLabelModel.ts).
  const onScreen = (i: number) => e.A[i] > 0.5 && (p.picture !== 'globe' || e.Z[i] >= 0) && e.X[i] > 30 && e.X[i] < W - 30 && e.Y[i] > p.stage.top && e.Y[i] < H - 14;
  const heaviest = (kind: 'group' | 'dir') => globe.nodes.filter((n) => n.struct === kind && onScreen(n.index)).sort((a, b) => b.weight - a.weight);
  const groups = heaviest('group');
  const dirs = heaviest('dir');
  const zoom = p.picture === 'globe' ? e.cam.zoom : e.flat.k / (e.fitK || 1);
  const budget = hubLabelBudget({ picture: p.picture === 'timeline' ? 'map' : p.picture, zoom, groups: groups.length, dirs: dirs.length });
  // The rim keeps every hub's name (the labels that would overlap are the ones dropped); at rest only the biggest few.
  const named = new Set<number>((p.map?.rim ? [...groups, ...dirs] : [...groups.slice(0, budget.groups), ...dirs.slice(0, budget.dirs)]).map((n) => n.index));
  const asked = (i: number) => i === p.selected || i === e.hover || i === p.pointed || !!p.found?.has(i) || !!p.focus?.has(i);
  for (const kind of ['layer', 'group', 'dir'] as const) {
    for (const n of globe.nodes) {
      if ((n.struct === 'theme' ? 'layer' : n.struct) !== kind) continue;
      const i = n.index;
      if (e.A[i] < 0.5) continue;
      if (p.picture === 'globe' && (e.Z[i] < 0 || kind === 'layer')) continue;
      const x = e.X[i];
      const y = e.Y[i] - e.R[i] - 7;
      if (x < 30 || x > W - 30 || y < p.stage.top || y > H - 14) continue;
      const alpha = dimmed(i) ? 0.3 : 1;
      if (kind === 'layer') {
        const text = n.struct === 'theme' ? n.label : LAYER_WORDS[n.layer];
        const w = text.length * 9 + 12;
        if (!free(x, y, w, 16)) continue;
        used.push({ x, y, w, h: 16 });
        const { w: tw, h } = drawLayerLabel(ctx, text, x, y, c.layer[n.layer], { alpha: 0.95 * alpha });
        hits.push({ layer: n.layer, x: x - tw / 2 - 6, y: y - h - 4, w: tw + 12, h: h + 12 });
      } else if (kind === 'group') {
        if (!named.has(i) && !asked(i)) continue;
        const label = n.label.length > 24 ? `${n.label.slice(0, 23)}…` : n.label;
        ctx.font = `600 12px ${p.font}`;
        const w = ctx.measureText(label).width + 8;
        if (!free(x, y, w, 14)) continue;
        used.push({ x, y, w, h: 14 });
        drawNameLabel(ctx, label, x, y, { colour: nodeColour(c, n.layer, n.deptIndex), size: 12, bold: true, alpha });
      } else if (((e.R[i] >= 9 && named.has(i)) || asked(i)) && kind === 'dir') {
        const label = `${n.label.length > 22 ? `${n.label.slice(0, 21)}…` : n.label}/`;
        const w = label.length * 6 + 8;
        if (!free(x, y, w, 13)) continue;
        used.push({ x, y, w, h: 13 });
        drawNameLabel(ctx, label, x, y, { colour: c.text, size: 10.5, alpha: 0.9 * alpha });
      }
    }
  }
}

/* ── Nodes ───────────────────────────────────────────────────────────── */

function drawNodes(ctx: CanvasRenderingContext2D, e: Engine, p: PaintInput): void {
  const { globe, colours: c } = p;
  const globeOn = p.picture === 'globe';
  const lit = new Set<number>();
  if (p.selected >= 0) {
    lit.add(p.selected);
    for (const cn of p.connections) lit.add(cn.index);
  }
  const emph = (i: number): boolean => (p.selected >= 0 ? lit.has(i) : p.found ? p.found.has(i) : p.focus ? p.focus.has(i) : true);
  const span = e.gR * e.cam.zoom * globe.outer || 1;
  const t = p.now;
  const back: number[] = [];
  const front: number[] = [];
  for (let i = 0; i < e.n; i++) {
    if (e.A[i] < 0.01) continue;
    if (globeOn && e.Z[i] < 0) back.push(i);
    else front.push(i);
  }
  const pass = (list: number[], isBack: boolean) => {
    const depthA = (i: number) => (globeOn ? 0.42 + 0.58 * Math.max(0, Math.min(1, (e.Z[i] / span + 1) / 2)) : 1);
    // Memory dots, batched by department and by whether they are lit.
    const buckets = new Map<string, { colour: string; alpha: number; idx: number[] }>();
    const single: number[] = [];
    for (const i of list) {
      const n = globe.nodes[i];
      const dim = !emph(i);
      const a = e.A[i] * (dim ? DIM : n.enabled ? 1 : 0.5) * depthA(i);
      if (n.layer === 'memory' && !n.struct && !isBubble(n.weight) && e.R[i] < 3.4 && e.A[i] > 0.98) {
        const col = nodeColour(c, n.layer, n.deptIndex);
        const aq = Math.round(a * 12) / 12;
        const key = `${n.deptIndex}|${aq}`;
        const b = buckets.get(key);
        if (b) b.idx.push(i);
        else buckets.set(key, { colour: col, alpha: Math.min(0.95, aq), idx: [i] });
      } else single.push(i);
    }
    for (const b of buckets.values()) {
      ctx.globalAlpha = b.alpha;
      ctx.fillStyle = b.colour;
      ctx.beginPath();
      for (const i of b.idx) {
        const r = e.R[i];
        if (r < 1.7) ctx.rect(e.X[i] - r, e.Y[i] - r, r * 2, r * 2);
        else {
          ctx.moveTo(e.X[i] + r, e.Y[i]);
          ctx.arc(e.X[i], e.Y[i], r, 0, TAU);
        }
      }
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    // Skills, then the rest by size so the big glyphs sit over the small ones.
    single.sort((a, b) => layerWeight(globe.nodes[a].layer) - layerWeight(globe.nodes[b].layer));
    for (const i of single) {
      const n = globe.nodes[i];
      const dim = !emph(i);
      const alpha = e.A[i] * (dim ? DIM : 1) * depthA(i);
      const style: NodeStyle = {
        layer: n.layer,
        colour: nodeColour(c, n.layer, n.deptIndex),
        x: e.X[i],
        y: e.Y[i],
        r: e.R[i],
        alpha,
        ...(n.glyph ? { glyph: n.glyph } : {}),
        t,
        seed: e.seed[i],
        weight: n.weight,
        disabled: !n.enabled,
        glow: !dim && !isBack && e.R[i] >= 6,
        big: e.R[i] > 4,
        motion: p.motion,
      };
      if (n.struct) {
        drawStruct(ctx, { ...style, struct: n.struct === 'theme' ? 'layer' : n.struct, open: isFoldable(globe, n) && p.expanded.has(n.key), count: n.struct === 'layer' ? (globe.counts[n.layer] ?? n.weight) : n.weight });
        continue;
      }
      switch (n.layer) {
        case 'apps':
          drawApp(ctx, style);
          break;
        case 'routines':
          drawRoutine(ctx, style);
          break;
        case 'skills':
          drawSkill(ctx, style);
          break;
        case 'memory':
          if (isBubble(n.weight) || e.R[i] >= 3.4) drawBubble(ctx, style);
          else {
            ctx.globalAlpha = alpha;
            ctx.fillStyle = style.colour;
            ctx.beginPath();
            ctx.arc(style.x, style.y, style.r, 0, TAU);
            ctx.fill();
            ctx.globalAlpha = 1;
          }
          break;
        default:
          drawOuter(ctx, style);
      }
    }
  };
  if (globeOn) {
    pass(back, true);
    pass(front, false);
  } else pass(front, false);
  void orbSprite;
  void bubbleFactor;
}

/** Skills at the back, then memory, then the big glyphs. */
function layerWeight(l: GlobeLayer): number {
  return l === 'skills' ? 0 : l === 'memory' ? 1 : l === 'routines' ? 2 : isRingLayer(l) && l !== 'apps' ? 3 : 4;
}

/** The selection's rings, the hovered node's ring, and the connected nodes' small halos. */
function drawMarks(ctx: CanvasRenderingContext2D, e: Engine, p: PaintInput): void {
  const { globe, colours: c } = p;
  for (const cn of p.connections) {
    const i = cn.index;
    if (e.A[i] < 0.3) continue;
    ctx.strokeStyle = rgba(nodeColour(c, globe.nodes[i].layer, globe.nodes[i].deptIndex), cn.hops === 1 ? 0.7 : 0.35);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(e.X[i], e.Y[i], e.R[i] + 3, 0, TAU);
    ctx.stroke();
  }
  const ring = (i: number, colour: string, width: number) => {
    if (i < 0 || e.A[i] < 0.3) return;
    ctx.strokeStyle = colour;
    ctx.lineWidth = width;
    ctx.beginPath();
    ctx.arc(e.X[i], e.Y[i], Math.max(6, e.R[i] * 1.35) + 2, 0, TAU);
    ctx.stroke();
  };
  if (p.pointed >= 0 && p.pointed !== p.selected) ring(p.pointed, c.text, 1.2);
  if (e.hover >= 0 && e.hover !== p.selected) ring(e.hover, c.text, 1.2);
  if (p.selected >= 0 && e.A[p.selected] > 0.3) {
    const n = globe.nodes[p.selected];
    drawSelection(ctx, e.X[p.selected], e.Y[p.selected], Math.max(5, e.R[p.selected]), c.accent, nodeColour(c, n.layer, n.deptIndex), p.now, p.motion);
  }
}

/** Names for the lit, and for the big glyphs once there is room: none over another. */
function drawNodeNames(ctx: CanvasRenderingContext2D, e: Engine, p: PaintInput): void {
  const { globe, colours: c } = p;
  const { W, H } = p.stage;
  const want: number[] = [];
  // A selection names what it connects to; a search names what it found; otherwise only the few biggest are named at rest,
  // and names of the rest appear once the picture is zoomed in past a threshold, up to a budget (memoryLabelModel.ts).
  if (p.selected >= 0) for (const cn of p.connections.slice(0, 14)) want.push(cn.index);
  else if (p.found && p.found.size <= 60) for (const i of p.found) want.push(i);
  else {
    const near: number[] = [];
    for (let i = 0; i < e.n; i++) if (e.A[i] > 0.9 && !globe.nodes[i].struct && (p.picture !== 'globe' || e.Z[i] > 0) && e.X[i] > 0 && e.X[i] < p.stage.W && e.Y[i] > 0 && e.Y[i] < p.stage.H) near.push(i);
    const zoom = p.picture === 'globe' ? e.cam.zoom : e.flat.k / (e.fitK || 1);
    const budget = labelBudget({ picture: p.picture, zoom, nodes: near.length });
    near.sort((x, y) => globe.nodes[y].weight - globe.nodes[x].weight);
    want.push(...near.slice(0, budget.leaves > 0 ? budget.leaves : budget.biggest));
  }
  // In the rim state the arcs are named; the things on them keep their names for the pointer, the selection's own link and a search.
  if (p.map?.rim && want.length > 3) want.length = 3;
  if (!want.length) return;
  const cells = new Set<string>();
  let placed = 0;
  for (const i of want) {
    if (placed >= 70) break;
    if (e.A[i] < 0.5 || i === p.selected || globe.nodes[i].struct) continue;
    const x = e.X[i];
    const y = e.Y[i];
    if (x < 24 || x > W - 24 || y < p.stage.top + 6 || y > H - 28) continue;
    const n = globe.nodes[i];
    const label = n.label.length > 26 ? `${n.label.slice(0, 25)}…` : n.label;
    const w = label.length * 5.8 + 8;
    const row = Math.floor((y + e.R[i] + 4) / 14);
    const keys: string[] = [];
    let free = true;
    for (let cx = Math.floor((x - w / 2) / 36); cx <= Math.floor((x + w / 2) / 36) && free; cx++) {
      for (const ry of [row, row + 1]) {
        if (cells.has(`${cx}:${ry}`)) {
          free = false;
          break;
        }
        keys.push(`${cx}:${ry}`);
      }
    }
    if (!free) continue;
    for (const k of keys) cells.add(k);
    drawNameLabel(ctx, label, x, y + e.R[i] + 13, { colour: c.text, size: 10.5, alpha: 0.9 });
    placed++;
  }
}

/** Name pills for the hovered, pointed-at and selected node. */
function drawPills(ctx: CanvasRenderingContext2D, e: Engine, p: PaintInput): void {
  const { globe, colours: c } = p;
  const { W } = p.stage;
  const pill = (i: number, accent: boolean) => {
    if (i < 0 || e.A[i] < 0.3) return;
    const n = globe.nodes[i];
    const label = n.label.length > 44 ? `${n.label.slice(0, 43)}…` : n.label;
    const sub = n.struct === 'dir' ? `Folder · ${n.weight} items` : n.struct === 'group' ? `${LAYER_WORDS[n.layer]} · ${n.weight} ${n.weight === 1 ? 'item' : 'items'}` : n.struct === 'layer' || n.struct === 'theme' ? `${n.struct === 'theme' ? 'Theme' : 'Layer'} · ${n.weight} items` : p.picture === 'timeline' ? changedWords(n.changedAt, p.wall) : `${LAYER_WORDS[n.layer]}${n.layer === 'memory' && n.dept ? ` · ${n.dept}` : n.dept && n.layer !== 'memory' ? ` · ${n.dept}` : ''}`;
    ctx.font = `600 12px ${p.font}`;
    const w = Math.max(ctx.measureText(label).width, ctx.measureText(sub).width * 0.9) + 20;
    const h = 40;
    const bx = Math.max(6, Math.min(W - w - 6, e.X[i] - w / 2));
    const by = Math.max(p.stage.top + 4, e.Y[i] - e.R[i] - h - 12);
    ctx.globalAlpha = 0.96;
    ctx.fillStyle = 'rgba(14,17,23,0.94)';
    ctx.strokeStyle = accent ? c.accent : 'rgba(255,255,255,0.18)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(bx, by, w, h, 12);
    ctx.fill();
    ctx.stroke();
    ctx.globalAlpha = 1;
    ctx.textAlign = 'left';
    ctx.fillStyle = c.text;
    ctx.fillText(label, bx + 10, by + 17);
    ctx.font = `400 11px ${p.font}`;
    ctx.fillStyle = c.muted;
    ctx.fillText(sub, bx + 10, by + 32);
  };
  if (p.pointed >= 0 && p.pointed !== p.selected) pill(p.pointed, false);
  if (e.hover >= 0 && e.hover !== p.selected && e.hover !== p.pointed) pill(e.hover, false);
  pill(p.selected, true);
}
